import type { Database } from '../../../types/database.types'

// Base photo type from database
export type Photo = Database['public']['Tables']['photos']['Row']
export type PhotoInsert = Database['public']['Tables']['photos']['Insert']
export type PhotoUpdate = Database['public']['Tables']['photos']['Update']

// Extended photo type with computed fields
export interface PhotoWithDetails extends Photo {
  sync_status?: SyncStatus
  stored_locally?: boolean
  local_blob_url?: string // Blob URL for offline photos
  compression_applied?: boolean
}

// Sync status for offline support
export type SyncStatus = 'synced' | 'pending' | 'syncing' | 'failed'

// Photo metadata extracted from EXIF
export interface PhotoMetadata {
  device_make?: string
  device_model?: string
  date_time_original?: string
  gps_latitude?: number
  gps_longitude?: number
  gps_altitude?: number
  orientation?: number
  flash_used?: boolean
  focal_length?: number
  iso?: number
  shutter_speed?: string
  aperture?: string
  white_balance?: string
}

// Photo upload options
export interface PhotoUploadOptions {
  compress?: boolean
  max_width?: number
  max_height?: number
  quality?: number
  generate_thumbnail?: boolean
  thumbnail_size?: number
  extract_exif?: boolean
}

/**
 * Default upload options.
 *
 * 1600px / q0.8 is not a quality compromise — it is exactly what
 * `pdfImageService` reduces every image to before embedding it, so the
 * bytes above this were re-encoded away at generation time and never
 * reached a report. Storing 1920px / q0.85 cost ~40% more per photo
 * (859 kB average across 1016 photos at the 2026-09-25 audit) for output
 * nothing reads. Keep this at or below `MAX_DIMENSION_PX` there; raising
 * it only grows the bucket.
 */
export const DEFAULT_PHOTO_UPLOAD_OPTIONS: PhotoUploadOptions = {
  compress: true,
  max_width: 1600,
  max_height: 1600,
  quality: 0.8,
  generate_thumbnail: true,
  thumbnail_size: 200,
  extract_exif: true,
}

// Photo capture data
export interface PhotoCaptureData {
  job_id: string
  node_id?: string
  file: File
  caption?: string
  gps_latitude?: number
  gps_longitude?: number
  metadata?: PhotoMetadata
}

// Photo update data
export interface PhotoUpdateData {
  caption?: string
  tags?: string[]
  is_primary?: boolean
}

// Compression result
export interface CompressionResult {
  compressed_file: File
  original_size: number
  compressed_size: number
  compression_ratio: number
}

// Thumbnail generation result
export interface ThumbnailResult {
  thumbnail_blob: Blob
  thumbnail_url: string
  width: number
  height: number
}

// Photo validation errors
export type PhotoValidationError =
  | 'INVALID_FILE_TYPE'
  | 'FILE_TOO_LARGE'
  | 'INVALID_DIMENSIONS'
  | 'COMPRESSION_FAILED'
  | 'EXIF_READ_FAILED'

export interface PhotoValidationResult {
  valid: boolean
  errors: PhotoValidationError[]
}

// Supported image MIME types
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
] as const

// Max file size (10MB)
export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024
